import {Injectable} from '@angular/core';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {IExpert} from './expert.interface';
import {BaseService, DataBaseServiceResponse, ServiceLoopbackFilters} from '../../services/base-service/base.service';
import {environment} from '../../../environments/environment';
import {RegisterExpertDTO, UpdateExpertInformationRequestDTO} from '../../dtos/expert';
import {IScheduleGroup} from '../timetable';
import {firstValueFrom, Observable} from 'rxjs';
import {IExpertsFilters} from './expert-filters.interface';
import {IExpertIndicators} from './expert-indicators.interface';
import {AnyObject} from '../../interfaces/utils';


export interface IRExpert {
  experts: IExpert[];
  count: number;
  selectedExpert: string;
  expertIndicators: IExpertIndicators;
  filters: IExpertsFilters;
}

export namespace ExpertAction {
  export class GetById {
    static readonly type = '[EXPERTS] Get Expert By Id Action';
    constructor(public id: string, public clinicId: string, public filters?: ServiceLoopbackFilters) { }
  }

  export class Post {
    static readonly type = '[EXPERTS] Register Expert Action';
    constructor(public clinicId: string, public user: RegisterExpertDTO) { }
  }

  export class Patch {
    static readonly type = '[EXPERTS] Patch Expert Action';
    constructor(public clinicId: string, public expertId: string, public expertDto: UpdateExpertInformationRequestDTO) { }
  }

  export class GetCount {
    static readonly type = '[PATIENTS] Get Experts Count Action';
    constructor(public clinicId: string, public search: string = '', public onlyAvailable: boolean) { }
  }

  export class GetCountByBranch {
    static readonly type = '[PATIENTS] Get Experts By Branch Count Action';
    constructor(public clinicId: string, public branchId: string, public search: string = '') { }
  }

  export class GetExpertProfile {
    static readonly type = '[EXPERTS] Get Expert Profile Id Action';
    constructor(public userId: string, public clinicId: string) { }
  }

  export class SetSelectedById {
    static readonly type = '[EXPERTS] Set Expert Selected Action';
    constructor(public value: string) { }
  }

  export class SetCount {
    static readonly type = '[EXPERTS] Set Experts Count Action';
    constructor(public value: number) { }
  }

  export class SetExperts {
    static readonly type = '[EXPERTS] Set Experts Action';
    constructor(public experts: IExpert[]) { }
  }


  export class PatchScheduleGroupExpert {
    static readonly type = '[EXPERTS] Patch Schedule Group Expert Action';
    constructor(public clinicId: string, public expertId: string, public overrideScheduleGroup: IScheduleGroup) { }
  }

  export class GetIndicators {
    static readonly type = '[EXPERTS] Get Expert Indicators Action';
    constructor(
      public clinicId: string, public expertId: string, public date: { year: number, month: number }, public timeZoneOverride: string
    ) { }
  }

  export class SetListFilters {
    static readonly type = '[EXPERTS] Set Expert List Filters Action';
    constructor(public filters: IExpertsFilters) { }
  }
}



@State<IRExpert>({
  name: 'ExpertState',
  defaults: {
    experts: [],
    count: 0,
    selectedExpert: null,
    expertIndicators: null,
    filters: {
      page: 1,
      search: null
    },
  }
})

@Injectable()
export class ExpertState {
  constructor(
    private baseService: BaseService<any>
  ) { }

  url = environment.SERVER_URL;

  @Selector()
  static getSelectedExpert(state: IRExpert): IExpert {
    return state.experts.find((expert: IExpert) => expert._id === state.selectedExpert);
  }

  @Selector()
  static getExperts(state: IRExpert): any[] {
    return state.experts;
  }

  @Selector()
  static getExpertsCount(state: IRExpert): number {
    return state.count;
  }

  @Selector()
  static getExpertIndicators(state: IRExpert): IExpertIndicators {
    return state.expertIndicators;
  }

  @Selector()
  public static getExpertByUserId(state: IRExpert): (id: string) => IExpert {
    return (id: string) => {
      return state.experts.find((item) => item.userId === id);
    };
  }

  @Selector()
  static getFilters(state: IRExpert): IExpertsFilters {
    return state.filters;
  }

  @Action(ExpertAction.GetById)
  async getExpertByIdAction({patchState}: StateContext<IRExpert>, action: ExpertAction.GetById): Promise<void> {
    const response: DataBaseServiceResponse<IExpert> = await firstValueFrom(this.baseService.get(`${this.url}/experts/${action.id}`, action.filters));
    if (response.error) throw new Error(response.message);

    patchState({
      selectedExpert: action.id,
      experts: [response.entity]
    });
  }

  @Action(ExpertAction.GetExpertProfile)
  async getExpertProfileAction({patchState}: StateContext<IRExpert>, {clinicId, userId}: ExpertAction.GetExpertProfile): Promise<void> {
    const params: AnyObject = {clinicId, userId};
    type RequestType = DataBaseServiceResponse<IExpert>;
    const request: Observable<RequestType> = this.baseService.get(
      `${this.url}/experts/profile`,
      void 0,
      void 0,
      {params}
    );
    const response: RequestType = await firstValueFrom(request);

    if (response.error) throw new Error(response.message);

    patchState({
      selectedExpert: userId,
      experts: [response.entity]
    });
  }

  @Action(ExpertAction.GetCount)
  async getExpertsCountAction(ctx: StateContext<IRExpert>, {clinicId, search, onlyAvailable}: ExpertAction.GetCount): Promise<void> {
    const params: AnyObject = {search, onlyAvailable};
    type RequestType = DataBaseServiceResponse<{count: number}>;
    const request: Observable<RequestType> = this.baseService.get(
      `${this.url}/clinics/${clinicId}/experts/count`,
      void 0,
      void 0,
      {params}
    );
    const response: RequestType = await firstValueFrom(request);
    if (response.error) throw new Error(response.message);

    ctx.patchState({
      count: response.entity && response.entity.count ? response.entity.count : 0
    });
  }

  @Action(ExpertAction.GetIndicators)
  async getExpertIndicatorsAction(
    {patchState}: StateContext<IRExpert>,
    {timeZoneOverride, date, expertId, clinicId}: ExpertAction.GetIndicators
  ): Promise<void> {
    type RequestType = DataBaseServiceResponse<IExpertIndicators>;
    const request: Observable<RequestType> = this.baseService.get(
      `${this.url}/clinics/${clinicId}/experts/${expertId}/appointments/month`,
      void 0,
      void 0,
      {
        params: {
          timeZoneOverride,
          date: JSON.stringify(date)
        }
      }
    );
    const response: RequestType = await firstValueFrom(request);

    if (response.error) throw new Error(response.message);

    patchState({
      expertIndicators: response.entity
    });

  }

  @Action(ExpertAction.GetCountByBranch)
  async getExpertsByBranchCountAction(
    {patchState}: StateContext<IRExpert>,
    {search, branchId, clinicId}: ExpertAction.GetCountByBranch
  ): Promise<void> {
    const params: AnyObject = {search};
    type RequestType = DataBaseServiceResponse<{count: number}>;
    const request: Observable<RequestType> = this.baseService.get(
      `${this.url}/clinics/${clinicId}/branches/${branchId}/experts/count`,
      void 0,
      void 0,
      {params}
    );
    const response: RequestType = await firstValueFrom(request);

    if (response.error) throw new Error(response.message);

    patchState({
      count: response.entity && response.entity.count ? response.entity.count : 0
    });
  }

  @Action(ExpertAction.SetSelectedById)
  async setExpertSelectedAction({patchState}: StateContext<IRExpert>, action: ExpertAction.SetSelectedById): Promise<void> {
    patchState({
      selectedExpert: action.value
    });
  }

  @Action(ExpertAction.SetExperts)
  async setExpertsAction(ctx: StateContext<IRExpert>, action: ExpertAction.SetExperts): Promise<void> {
    ctx.patchState({
      experts: action.experts
    });
  }

  @Action(ExpertAction.Post)
  async registerExpertAction(ctx: StateContext<IRExpert>, action: ExpertAction.Post): Promise<void> {
    type RequestType = DataBaseServiceResponse<IExpert>;
    const request: Observable<RequestType> = this.baseService.post(`${this.url}/auth/register/${action.clinicId}/expert`, action.user);
    const response: RequestType = await firstValueFrom(request);

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      experts: [...ctx.getState().experts, response.entity]
    });
  }

  @Action(ExpertAction.Patch)
  async patchExpertAction(ctx: StateContext<IRExpert>, action: ExpertAction.Patch): Promise<void> {
    type RequestType = DataBaseServiceResponse<void>;
    const request: Observable<RequestType> = this.baseService.patch(`${this.url}/experts/${action.expertId}`, action.expertDto);
    const response: RequestType = await firstValueFrom(request);

    if (response.error) throw new Error('No se ha podido actualizar el usuario experto');
  }

  @Action(ExpertAction.PatchScheduleGroupExpert)
  async patchScheduleGroupExpertAction(
    ctx: StateContext<IRExpert>,
    {overrideScheduleGroup, expertId}: ExpertAction.PatchScheduleGroupExpert
  ): Promise<void> {
    type RequestType = DataBaseServiceResponse<void>;
    const request: Observable<RequestType> = this.baseService.patch(`${this.url}/experts/${expertId}`, {overrideScheduleGroup});
    const response: RequestType = await firstValueFrom(request);

    if (response.error) throw new Error('No se ha podido actualizar el grupo de horarios del usuario experto');
  }

  @Action(ExpertAction.SetCount)
  async setExpertsCountAction(ctx: StateContext<IRExpert>, action: ExpertAction.SetCount): Promise<void> {
    ctx.patchState({
      count: action.value
    });
  }

  @Action(ExpertAction.SetListFilters)
  async setExpertListFiltersAction(ctx: StateContext<IRExpert>, action?: ExpertAction.SetListFilters): Promise<void> {
    ctx.patchState({
      filters: action.filters
    });
  }
}
